import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TableCDS } from '@sharedCR/coreDesignSystem/Table';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getTaskSlaGroupAction } from 'store/loanApplications/actions';
import { selectTaskSlaGroupLevelsData } from 'store/loanApplications/selectors';

import { buildSlaDurationLabel } from './SlaGroupInfoModal.utils';
import { taskSlaTableConfig } from './TaskSlaGroupInfoModal.config';
import { TaskSlaLevelColumnId } from './TaskSlaGroupInfoModal.constants';
import { formatTaskSlaGroupLevels, getActiveRowStyles } from './TaskSlaGroupInfoModal.utils';

const TaskSlaGroupInfoModal = ({ slaGroupId, isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const levels = useSelector(selectTaskSlaGroupLevelsData(slaGroupId));

  useEffectSafeStrictMode(() => {
    setIsLoading(true);
    dispatch(getTaskSlaGroupAction({ taskSlaGroupId: slaGroupId })).finally(() =>
      setIsLoading(false),
    );
  }, [dispatch, setIsLoading, slaGroupId]);

  const rows = useMemo(() => {
    const formatted = formatTaskSlaGroupLevels(levels);

    return formatted.map(level => ({
      id: level.id,
      [TaskSlaLevelColumnId.SLA]: buildSlaDurationLabel({ slaDuration: level.duration }),
      [TaskSlaLevelColumnId.POTENTIAL_ASSIGNEES]: level.potentialAssigneesFullNames,
      [TaskSlaLevelColumnId.ASSIGNED_USERS]: level.assignedUsersFullNames,
      [TaskSlaLevelColumnId.ASSIGNMENT_TYPE]: level.type,
      [TaskSlaLevelColumnId.START_DATE]: level.startDate,
      _level: level,
      _deletedPotential: level.deletedPotentialAssigneesFullNames,
      _deletedAssigned: level.deletedAssignedUsersFullNames,
      _rowStyles: getActiveRowStyles({ isActive: level.isActive }),
    }));
  }, [levels]);

  if (isLoading) {
    return null;
  }

  return <TableCDS withNoAvailableMessage config={taskSlaTableConfig} data={rows} />;
};

TaskSlaGroupInfoModal.propTypes = {
  ...WithLoadingPropTypes,
  slaGroupId: PropTypes.string.isRequired,
};

export default compose(withLoading, memo)(TaskSlaGroupInfoModal);
